<script setup lang="ts">
import { getMatchedCityQuery } from '@features/cities/services/formatCitiesList'
import type { FormattedCity } from '@features/cities/types'
import { useMiniapps } from '@shared/nuxt/useMiniapps'

const props = withDefaults(
  defineProps<{
    activeSearch?: boolean
    searchQuery?: string
    formattedCitiesList: FormattedCity[]
  }>(),
  {
    activeSearch: false,
    searchQuery: '',
  },
)

const cityTitle = 'Перейти на сайт Farfor в городе '
const { telegramChatId, vkUserId } = useMiniapps()
const { YM_ID } = useRuntimeConfig().public

const getHighlightedSegments = (cityName: string) => {
  const matchedQuery = getMatchedCityQuery(cityName, props.searchQuery)

  if (!matchedQuery) {
    return [{ text: cityName, highlighted: false }]
  }

  const escapedQuery = matchedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const segments = cityName.split(new RegExp(`(${escapedQuery})`, 'gi'))

  return segments.filter(Boolean).map((text) => ({
    text,
    highlighted: text.toLowerCase() === matchedQuery.toLowerCase(),
  }))
}

const itemBeforeLeave = (element: HTMLElement) => {
  element.style.height = `${element.clientHeight}px`
}

const onClickCityLink = (link: string | undefined) => {
  if (!link) return

  window.location.href = link
}
</script>

<template>
  <div
    id="citiesList"
    class="cities-list-container"
    :class="{ 'cities-list-container--active': activeSearch }"
    data-test-id="cities-list-container"
  >
    <transition-group
      name="collapse-fade"
      tag="div"
      class="cities-list-container__list anim-slide-fade-up"
      :class="{ 'anim-slide-fade-up--active': activeSearch }"
      data-test-id="cities-list"
      @before-leave="itemBeforeLeave($event as HTMLElement)"
    >
      <div
        v-for="city in formattedCitiesList"
        :key="city.domainPrefix"
        class="cities-list-container__item"
        data-test-id="city-item"
      >
        <span
          v-if="city?.firstLetter"
          class="cities-list-container__first-letter"
          data-test-id="city-first-letter"
        >
          {{ city.firstLetter }}
        </span>

        <a
          class="cities-list-container__link"
          :href="city.cityUrl"
          :title="cityTitle + city.visibleName"
          data-test-id="city-link"
          @click.prevent="onClickCityLink(city.cityUrl)"
        >
          <span
            v-for="(segment, index) in getHighlightedSegments(city.visibleName)"
            :key="`${segment.text}-${index}`"
            :class="{
              'cities-list-container__highlighted-text': segment.highlighted,
            }"
          >
            {{ segment.text }}
          </span>
        </a>
      </div>
    </transition-group>
  </div>
</template>

<style scoped lang="scss">
.cities-list-container {
  height: 0;
  margin-top: 0;
  overflow: hidden;
  opacity: 0;
  transition: 0.1s;

  &--active {
    height: auto;
    margin-top: 10px;
    opacity: 1;

    @include media($bp-tablet) {
      height: 540px;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    width: 100%;
    max-width: 100%;
    height: auto;

    @include media($bp-desktop-mini) {
      width: max-content;
      height: 100%;
    }
  }

  &__item {
    position: relative;
    width: 100%;
    padding-left: 20px;
    font-size: 14px;
    line-height: 30px;

    @include media($bp-tablet) {
      width: calc(100% / 4);
    }

    @include media($bp-desktop-mini) {
      width: 170px;
    }
  }

  &__first-letter {
    position: absolute;
    left: 0;
    font-size: 14px;
    font-weight: 500;
    color: var(--ui-color-primary-500);
  }

  &__link {
    font-size: 14px;
    font-weight: 500;
    line-height: 30px;
    color: var(--ui-color-supporting-01);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__highlighted-text {
    color: var(--ui-color-primary-500);
  }
}
</style>
